import deepEqual from 'fast-deep-equal';
import { useReducer, useEffect, useRef, Dispatch, SetStateAction } from 'react';

import useEventCallback from '../useEventCallback/index.ts';
import usePrevious from '../usePrevious/index.ts';

const FORCE_UPDATE = Symbol('force-update');
export type SetAction<T> = (currentState: T) => T;
export interface OverriddenStateOptions<T> {
  /**
   * Allows reducing what was passed into `dispatch` with the current state
   */
  reducer?: (currentState: T, a: T | SetAction<T>) => T;
  /**
   * Initializes the `initialValue` like `React.useReducer`
   * @default "a => a"
   */
  init?: (initialState: T) => T;
  /**
   * Whenever the setState callback is fired this will be fired with whatever it fired with
   */
  onChange?: ((currentState: T) => void) | null;
  /**
   * Allows you to determine the criteria for using the second parameter of this function
   * @default "p => p !== null && p !== undefined"
   */
  shouldUseOverride?: (overrideWith: T | null) => overrideWith is T;
  comparator?: (a?: T | null, b?: T | null) => boolean;
}
/**
 * Equivalent of `dispatch` returned by `React.useReducer`
 */
type UseOverriddenStateSetState<T> = Dispatch<SetStateAction<T>>;
/**
 * If only the initialValue is provided, then the component is considered uncontrolled.
 * This means the component owns its state communicating changes view the `onChange` callback
 */
type UseOverriddenStateInitialValue<T> = T | undefined;
/**
 * If only the overrideWith is provided, then the component is considered controlled.
 * This means the component uses whatever state is passed in.
 * Any changes made by calling the `dispatch` is communicated via `onChange`
 * @default null
 */
type UseOverriddenStateOverrideWithValue<T> = T | null | undefined;
interface ForceUpdate<T> {
  type: symbol;
  payload: T;
}

function isForceUpdate<T>(x: T | SetAction<T> | ForceUpdate<T>): x is ForceUpdate<T> {
  return typeof x === 'object' && x !== null && 'type' in x && x.type === FORCE_UPDATE;
}

/**
 * This hook allows a state to both be _controlled_ and _uncontrolled_ based on the values passed.
 * By default if `overrideWith` is one of `null, '', undefined` the state is _uncontrolled_
 *
 * @example <caption>Example Usage</caption>
 * const [value, setValue] = useOverriddenState(initValue, value, { onChange });
 */
export default function useOverriddenState<T>(
  initialValue: UseOverriddenStateInitialValue<T>,
  overrideWith: UseOverriddenStateOverrideWithValue<T> = null,
  {
    reducer = (s: T, a: T | SetAction<T>) =>
      typeof a === 'function' ? (a as (currentState: T) => T)(s) : a,
    init = is => is,
    onChange: onChangeFromProps,
    shouldUseOverride = (p: unknown): p is T => p !== null && p !== undefined,
    comparator = (a?: T | null, b?: T | null): boolean => deepEqual(a, b)
  }: OverriddenStateOptions<T> = {}
): [T, UseOverriddenStateSetState<T>] {
  function wrapReducer(s: T, a: SetStateAction<T> | ForceUpdate<T>): T {
    if (isForceUpdate(a)) {
      return a.payload;
    }

    return reducer(s, a);
  }

  const hasBeenCalled = useRef(false);
  const shouldOverride = shouldUseOverride(overrideWith);
  const wasPreviouslyControlled = usePrevious(shouldOverride);
  const onChange = useEventCallback(onChangeFromProps || (() => 0));
  const [state, dispatch] = useReducer(
    wrapReducer,
    shouldOverride ? (overrideWith as T) : (initialValue as T),
    init
  );

  let returnedState = state;

  if (shouldOverride) {
    returnedState = overrideWith as T;
  }

  useEffect(() => {
    if (hasBeenCalled.current) {
      onChange(state);
    } else {
      // Skip updating onChange for first render or a force update as nothing has changed
      hasBeenCalled.current = true;
    }
  }, [state, onChange, hasBeenCalled]);

  useEffect(() => {
    if (shouldUseOverride(overrideWith) && !comparator(state, overrideWith)) {
      hasBeenCalled.current = false;

      dispatch({
        type: FORCE_UPDATE,
        payload: overrideWith
      });
    }
  }, [dispatch, state, overrideWith, hasBeenCalled, comparator, shouldUseOverride]);

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (wasPreviouslyControlled !== undefined) {
        if (wasPreviouslyControlled && !shouldOverride) {
          console.warn(
            'WARNING: trying to convert a controlled component to an uncontrolled component - it is probably better to maintain control'
          );
        } else if (!wasPreviouslyControlled && shouldOverride) {
          console.warn(
            'WARNING: trying to convert an uncontrolled component to a controlled component - it is probably better to maintain control'
          );
        }
      }
    }, [wasPreviouslyControlled, shouldOverride]);
  }
  return [returnedState, dispatch];
}

/**
 * Properties that apply for a key when being used in a controlled manner.
 */
export type ControlledProperty<
  PropName extends string,
  PropType,
  InitialPropertyName extends string = `initial${Capitalize<PropName>}`,
  OnChangePropertyName extends string = 'onChange'
> = Record<PropName, PropType> &
  Partial<Record<InitialPropertyName, never>> &
  Record<OnChangePropertyName, (value: PropType) => void>;

/**
 * Properties that apply for a key when being used in a uncontrolled manner.
 */
export type UncontrolledProperty<
  PropName extends string,
  PropType,
  InitialPropertyName extends string = `initial${Capitalize<PropName>}`,
  OnChangePropertyName extends string = 'onChange'
> = Partial<Record<PropName, never>> &
  Record<InitialPropertyName, PropType> &
  Partial<Record<OnChangePropertyName, (value: PropType) => void>>;

/**
 * Properties that apply for a key when being used in either controlled or uncontrolled manner, mutually exclusively.
 * @example
 *
 * ```tsx
 * type IsOpenProps = ControlledOrUncontrolledProp<'isOpen', boolean>;
 * // equivalent to
 * type IsOpenProps =
 *  | { isOpen: boolean;   initialIsOpen:  undefined; onChange:  (value: boolean) => void; }
 *  | { isOpen: undefined; initialIsOpen?: boolean;   onChange?: (value: boolean) => void; }
 * ```
 */
export type ControlledOrUncontrolledProp<
  PropName extends string,
  PropType,
  InitialPropertyName extends string = `initial${Capitalize<PropName>}`,
  OnChangePropertyName extends string = 'onChange'
> =
  | ControlledProperty<PropName, PropType, InitialPropertyName, OnChangePropertyName>
  | UncontrolledProperty<PropName, PropType, InitialPropertyName, OnChangePropertyName>;
